/**
 * Shared utility functions for the Lego design system.
 * Importable by any package that depends on @lucid/lego.
 */

/*
|--------------------------------------------------------------------------
| CURRENCY HELPERS
|--------------------------------------------------------------------------
*/

/**
 * Format a numeric amount as a locale-aware currency string using a currency code.
 * Returns '—' when the amount is undefined (for display in tables/lists).
 * @param amount - Numeric amount to format, or undefined
 * @param currencyCode - ISO 4217 currency code (default: 'USD')
 * @returns Formatted currency string (e.g. '$1,234.56') or '—' when undefined
 * @example
 * ```ts
 * formatCurrencyByCode(1234.56)         // '$1,234.56'
 * formatCurrencyByCode(1234.56, 'EUR')  // '€1,234.56'
 * formatCurrencyByCode(undefined)       // '—'
 * ```
 */
export const formatCurrencyByCode = (amount: number | undefined, currencyCode = 'USD'): string => {
  if (amount === undefined) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).format(
    amount
  );
};

/**
 * Get the currency code for a given locale
 * @param locale - The locale string (e.g., 'en-us', 'de-de')
 * @returns ISO 4217 currency code
 */
export const getCurrencyCodeForLocale = (locale: string): string => {
  if (!locale) return 'EUR';
  const normalizedLocale = locale.toLowerCase();
  switch (normalizedLocale) {
    case 'en':
    case 'en-us':
      return 'USD';
    case 'en-ca':
    case 'fr-ca':
      return 'CAD';
    case 'de-de':
    case 'nl-nl':
      return 'EUR';
    case 'nb-no':
      return 'NOK';
    case 'ar-sa':
    case 'en-sa':
      return 'SAR';
    case 'en-ae':
    case 'ar-ae':
      return 'AED';
    case 'fr-ch':
    case 'de-ch':
      return 'CHF';
    default:
      return 'USD';
  }
};

/**
 * Get currency symbol for a currency code
 * @param currencyCode - ISO 4217 currency code
 * @returns Currency symbol
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  const symbols: Record<string, string> = {
    USD: '$',
    CAD: '$',
    EUR: '€',
    NOK: 'kr',
    SAR: 'ر.س',
    AED: 'د.إ',
    CHF: 'CHF',
  };
  return symbols[currencyCode] || currencyCode;
};

export type CurrencyDetails = {
  currencyCode: string;
  currencySymbol: string;
  prefix: string;
  prefixSpace: boolean;
  suffix: string;
  fractionalSeparator: string;
};

/**
 * Get detailed currency formatting information for a locale
 * @param locale - The locale string
 * @returns Currency formatting details
 */
export const getCurrencyDetailsForLocale = (locale: string): CurrencyDetails => {
  if (!locale) return getCurrencyDetailsForLocale('de-de');
  const normalizedLocale = locale.toLowerCase();
  const code = getCurrencyCodeForLocale(normalizedLocale);
  const symbol = getCurrencySymbol(code);

  let prefix = '';
  let prefixSpace = false;
  let suffix = '';
  let fractionalSeparator = '.';

  switch (normalizedLocale) {
    case 'en':
    case 'en-us': // $123,456.78
      prefix = symbol;
      prefixSpace = false;
      fractionalSeparator = '.';
      break;
    case 'en-ca': // $123,456.78 CAD
      prefix = symbol;
      prefixSpace = false;
      suffix = code;
      fractionalSeparator = '.';
      break;
    case 'fr-ca': // 123 456,78 CAD
      suffix = code;
      fractionalSeparator = ',';
      break;
    case 'de-de': // 123.456,78 €
      suffix = symbol;
      fractionalSeparator = ',';
      break;
    case 'nl-nl': // €123.456,78
      prefix = symbol;
      prefixSpace = false;
      fractionalSeparator = ',';
      break;
    case 'nb-no': // kr 123 456,78
      prefix = symbol;
      prefixSpace = true;
      fractionalSeparator = ',';
      break;
    case 'de-ch': // CHF 123'456.78
      prefix = code;
      prefixSpace = true;
      fractionalSeparator = '.';
      break;
    case 'fr-ch': // 123 456.78 CHF
      suffix = code;
      fractionalSeparator = '.';
      break;
    case 'en-sa': // SAR 123,456.78
      prefix = code;
      prefixSpace = true;
      fractionalSeparator = '.';
      break;
    case 'ar-sa': // ر.س 123,456.78
      prefix = symbol;
      prefixSpace = true;
      fractionalSeparator = '.';
      break;
    case 'en-ae': // AED 123,456.78
      prefix = code;
      prefixSpace = true;
      fractionalSeparator = '.';
      break;
    case 'ar-ae': // د.إ 123,456.78
      prefix = symbol;
      prefixSpace = true;
      fractionalSeparator = '.';
      break;
    default:
      prefix = symbol;
      prefixSpace = false;
      fractionalSeparator = '.';
      break;
  }

  return {
    currencyCode: code,
    currencySymbol: symbol,
    prefix,
    prefixSpace,
    suffix,
    fractionalSeparator,
  };
};

export type FormattedPrice = {
  locale: string;
  integerAmount: string;
  fractionalAmount: string;
  formattedPriceWithCents: string;
  formattedPriceWithoutCents: string;
  priceWithCents: string;
  priceWithoutCents: string;
  currencyCode: string;
  currencySymbol: string;
};

type LocalePriceValue = string | number | undefined | null;

/**
 * Parses a numeric string that may be locale-formatted into a JavaScript number.
 * Handles both raw dot-decimal strings ("5488.6") and locale-formatted strings
 * ("5.488,6" for de-DE or "5,488.6" for en-US).
 * @param value - A numeric string, potentially locale-formatted
 * @returns The parsed number, or 0 if the value is empty/unparseable
 * @example
 * ```ts
 * parseLocalePrice('5488.6')    // 5488.6  (raw dot-decimal)
 * parseLocalePrice('5.488,6')   // 5488.6  (de-DE formatted)
 * parseLocalePrice('5,488.6')   // 5488.6  (en-US formatted)
 * parseLocalePrice('1.234.567') // 1234567 (de-DE thousands only)
 * parseLocalePrice('')          // 0
 * ```
 */
export const parseLocalePrice = (value: LocalePriceValue): number => {
  if (!value) return 0;

  if (typeof value === 'number') return value;

  // If it's already a valid JS number, use it directly
  const direct = Number(value);
  if (!Number.isNaN(direct)) return direct;

  // Strip everything except digits, dots, commas, and leading minus
  const cleaned = value.replace(/[^\d.,-]/g, '');
  if (!cleaned) return 0;

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');

  if (lastComma > lastDot) {
    // Comma is the decimal separator (e.g., "5.488,6" → de-DE)
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
  }

  // Dot is the decimal separator (e.g., "5,488.6") or no separator at all
  return parseFloat(cleaned.replace(/,/g, '')) || 0;
};

/**
 * Format a price according to locale-specific conventions.
 * @param rawPrice - The raw price (number, string, or undefined)
 * @param locale - The locale string (e.g., 'en-us', 'de-de')
 * @returns Formatted price object with various formatting options
 * @example
 * ```ts
 * formatLocalePrice(123456.78, 'en-us')
 * // { formattedPriceWithCents: '$123,456.78 USD', ... }
 *
 * formatLocalePrice(123456.78, 'de-de')
 * // { formattedPriceWithCents: '123.456,78 €', ... }
 * ```
 */
export const formatLocalePrice = (
  rawPrice: string | number | null | undefined,
  locale: string
): FormattedPrice => {
  const price = typeof rawPrice === 'string' ? parseFloat(rawPrice) || 0 : (rawPrice ?? 0);

  const normalizedLocale = (locale || 'de-de').toLowerCase();
  const localeCurrencyDetails = getCurrencyDetailsForLocale(normalizedLocale);

  const currencyFormatWithCents: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: localeCurrencyDetails.currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  const currencyFormatWithoutCents: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: localeCurrencyDetails.currencyCode,
    maximumFractionDigits: 0,
  };

  let formattedPriceWithCents = '';
  let formattedPriceWithoutCents = '';
  let priceWithCents = '';
  let priceWithoutCents = '';

  const intlLocale = normalizedLocale === 'en' ? 'en-US' : normalizedLocale;

  switch (normalizedLocale) {
    case 'en':
    case 'en-us':
      formattedPriceWithCents = `${price.toLocaleString('en-US', currencyFormatWithCents)} USD`;
      formattedPriceWithoutCents = `${price.toLocaleString('en-US', currencyFormatWithoutCents)} USD`;
      priceWithCents = price.toLocaleString('en-US', currencyFormatWithCents);
      priceWithoutCents = price.toLocaleString('en-US', currencyFormatWithoutCents);
      break;
    case 'en-ca':
      formattedPriceWithCents = `${price.toLocaleString(intlLocale, currencyFormatWithCents)} CAD`;
      formattedPriceWithoutCents = `${price.toLocaleString(intlLocale, currencyFormatWithoutCents)} CAD`;
      priceWithCents = price.toLocaleString(intlLocale, currencyFormatWithCents);
      priceWithoutCents = price.toLocaleString(intlLocale, currencyFormatWithoutCents);
      break;
    case 'fr-ca':
      formattedPriceWithCents = price.toLocaleString(intlLocale, {
        ...currencyFormatWithCents,
        currencyDisplay: 'code',
      });
      formattedPriceWithoutCents = price.toLocaleString(intlLocale, {
        ...currencyFormatWithoutCents,
        currencyDisplay: 'code',
      });
      priceWithCents = price.toLocaleString(intlLocale, currencyFormatWithCents);
      priceWithoutCents = price.toLocaleString(intlLocale, currencyFormatWithoutCents);
      break;
    case 'ar-sa':
      formattedPriceWithCents = `${localeCurrencyDetails.currencySymbol} ${price.toLocaleString(
        'en-AE',
        { ...currencyFormatWithCents, style: 'decimal' }
      )}`;
      formattedPriceWithoutCents = `${localeCurrencyDetails.currencySymbol} ${price.toLocaleString(
        'en-AE',
        { ...currencyFormatWithoutCents, style: 'decimal' }
      )}`;
      priceWithCents = formattedPriceWithCents;
      priceWithoutCents = formattedPriceWithoutCents;
      break;
    case 'en-ae':
    case 'en-sa':
      formattedPriceWithCents = price.toLocaleString(intlLocale, {
        ...currencyFormatWithCents,
        currencyDisplay: 'code',
      });
      formattedPriceWithoutCents = price.toLocaleString(intlLocale, {
        ...currencyFormatWithoutCents,
        currencyDisplay: 'code',
      });
      priceWithCents = price.toLocaleString(intlLocale, currencyFormatWithCents);
      priceWithoutCents = price.toLocaleString(intlLocale, currencyFormatWithoutCents);
      break;
    case 'ar-ae':
      formattedPriceWithCents = price.toLocaleString(intlLocale, currencyFormatWithCents);
      formattedPriceWithoutCents = price.toLocaleString(intlLocale, currencyFormatWithoutCents);
      priceWithCents = formattedPriceWithCents;
      priceWithoutCents = formattedPriceWithoutCents;
      break;
    default:
      formattedPriceWithCents = price.toLocaleString(intlLocale, currencyFormatWithCents);
      formattedPriceWithoutCents = price.toLocaleString(intlLocale, currencyFormatWithoutCents);
      priceWithCents = formattedPriceWithCents;
      priceWithoutCents = formattedPriceWithoutCents;
      break;
  }

  const integerAmount = price.toLocaleString(normalizedLocale === 'ar-sa' ? 'ar-SA' : intlLocale, {
    style: 'decimal',
    maximumFractionDigits: 0,
  });

  const fixedAmount = Number(price).toFixed(2);
  const fractionalAmount = fixedAmount.split('.')[1] || '00';

  return {
    locale: normalizedLocale,
    integerAmount,
    fractionalAmount,
    formattedPriceWithCents,
    formattedPriceWithoutCents,
    priceWithCents,
    priceWithoutCents,
    currencyCode: localeCurrencyDetails.currencyCode,
    currencySymbol: localeCurrencyDetails.currencySymbol,
  };
};

/**
 * Format a number as a locale-aware currency string.
 * @param value - The numeric value to format
 * @param locale - Locale for formatting (default: 'en-US')
 * @returns Formatted currency string (e.g., "$1,234.56")
 * @example
 * ```ts
 * formatCurrency(1234.56)          // "$1,234.56"
 * formatCurrency(1000, 'de-de')    // "1.000,00 €"
 * ```
 */
export const formatCurrency = (
  value: string | number | null | undefined,
  locale = 'en-US'
): string => {
  return formatLocalePrice(value, locale).priceWithCents;
};
